import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { bmxurl, ConvertDateToDB, extraAvailableOnDate } from 'src/globals';
import { environment } from 'src/environments/environment';
import { BookingPrefillService, bookingprefill } from './booking-prefill.service';
import { questionAnswersOn } from './booking-answers';
import { MoveBookingService } from './move-booking.service';
import { BookingChangesService } from './booking-changes.service';
import { BookingEditService, bookingforedit } from './booking-edit.service';
import { PackageAssistantService, packageproposal } from './package-assistant.service';

//A change the assistant has proposed, waiting for staff to confirm it in the panel. The assistant
//never makes a change itself - only the Confirm button on the card does.
export interface assistantaction {
  kind: 'move' | 'admissions' | 'package' | 'booking';
  //What the card is headed and shows while saving.
  title: string;
  icon: string;
  workingText: string;
  bookingId: string;
  salesId: string;
  summary: string;
  //A move: the new day and time.
  day?: Date;
  time?: string;
  //A change of admissions: the new number for each admissionid.
  counts?: { [admissionid: string]: number };
  //A package or time set change: what Confirm runs (PackageAssistantService works it out).
  apply?: () => Promise<string>;
  //A new booking: what Make a Booking is filled in with when Confirm opens it.
  prefill?: bookingprefill;
  hasEmail: boolean;
  status: 'pending' | 'working' | 'done' | 'failed' | 'cancelled';
  message?: string;
  email?: 'sending' | 'sent' | 'failed';
}

//One line in the assistant panel.
export interface assistantentry {
  role: 'user' | 'assistant' | 'status' | 'error' | 'action';
  text: string;
  action?: assistantaction;
}

//The AI staff assistant - PROTOTYPE. It reads freely; the changes it can make - moving a
//booking, changing its admissions, and creating or fixing packages and time sets - it only
//proposes, on a card that staff confirm.
//
//The conversation runs here in the browser: each turn goes to the local relay
//(tools/ai-assistant-relay), which adds the API key, the instructions and the tool list and passes
//it to Claude. When Claude asks for a tool, it is carried out here with the Dashboard's ordinary API
//calls - so with the staff member's own login, and never anything they could not do by hand - and
//the results go back on the next turn.
//
//The conversation is kept exactly as Claude returned it (every content block, unchanged), because
//the API needs earlier turns sent back as they were.
@Injectable({
  providedIn: 'root'
})
export class AssistantService {

  //What the panel shows.
  entries: assistantentry[] = [];
  working: boolean = false;

  //The conversation as sent to Claude.
  private messages: any[] = [];

  //What staff did with proposed changes since the last question - told to Claude with the next
  //one, so it knows whether a move it proposed went ahead.
  private notes: string[] = [];

  //A runaway loop of tool calls is cut off rather than left running up a bill.
  private readonly maxRounds = 12;

  //The venue the conversation is about. Entering another venue starts a fresh one, so nothing
  //read at one venue is carried into another's.
  private conversationVenue: string = null;

  constructor(private http: HttpClient, private moveBookingService: MoveBookingService, private bookingChanges: BookingChangesService,
    private bookingEditService: BookingEditService, private packageAssistant: PackageAssistantService,
    private bookingPrefill: BookingPrefillService, private router: Router) { }

  //Called when the panel is shown.
  checkVenue(): void {
    if (this.conversationVenue != null && this.conversationVenue != this.venueId && !this.working) {
      this.clear();
    }

    this.conversationVenue = this.venueId;
  }

  //Needs a relay to talk to and the venue's AIAssistant setting switched on (off unless set).
  //Read every time, as app.component reads Snowing: the settings arrive in localStorage after
  //sign in and change when another venue is entered.
  get enabled(): boolean {
    return (environment.aiAssistantUrl ?? '') != '' && localStorage.getItem('AIAssistant') == 'true';
  }

  private get venueId(): string {
    return localStorage.getItem('SmartVenueID') ?? '';
  }

  clear(): void {
    this.entries = [];
    this.messages = [];
    this.notes = [];
  }

  async ask(question: string): Promise<void> {
    const text = (question ?? '').trim();

    if (text == '' || this.working || !this.enabled) {
      return;
    }

    this.checkVenue();
    this.working = true;
    this.entries.push({ role: 'user', text: text });

    //If this turn fails part way, the conversation goes back to how it was before it, so the next
    //question does not carry a half-finished exchange the API would reject.
    const before = this.messages.length;

    //Today's date and the venue go in the conversation rather than the relay's instructions, which
    //stay the same on every request so they can be cached.
    const opening = this.messages.length == 0 ? this.contextLine() + '\n\n' : '';
    const notes = this.notes.length > 0 ? '(' + this.notes.join(' ') + ')\n\n' : '';
    this.notes = [];
    this.messages.push({ role: 'user', content: [{ type: 'text', text: opening + notes + text }] });

    try {
      await this.runTurn();
    }
    catch (error: any) {
      this.messages.length = before;
      this.removeStatus();
      this.entries.push({ role: 'error', text: this.errorText(error) });
    }
    finally {
      this.working = false;
    }
  }

  private async runTurn(): Promise<void> {
    for (let round = 0; round < this.maxRounds; round++) {
      const reply: any = await this.http.post(environment.aiAssistantUrl + '/chat', { messages: this.messages }).toPromise();

      //Every block goes back unchanged on the next request, including the model's thinking.
      this.messages.push({ role: 'assistant', content: reply.content });

      if (reply.stop_reason == 'tool_use') {
        const calls = (reply.content ?? []).filter((block: any) => block.type == 'tool_use');
        this.showStatus(calls);

        //All the results go back together, in one message.
        const results = await Promise.all(calls.map((call: any) => this.runTool(call)));
        this.messages.push({ role: 'user', content: results });
        continue;
      }

      //A long server-side step can pause a turn; sending the conversation back carries on with it.
      if (reply.stop_reason == 'pause_turn') {
        continue;
      }

      this.removeStatus();

      const answer = (reply.content ?? [])
        .filter((block: any) => block.type == 'text')
        .map((block: any) => block.text)
        .join('\n')
        .trim();

      if (reply.stop_reason == 'refusal') {
        this.entries.push({ role: 'error', text: 'The assistant could not help with that request.' });
        return;
      }

      this.entries.push({ role: 'assistant', text: answer != '' ? answer : 'No answer came back.' });

      if (reply.stop_reason == 'max_tokens') {
        this.entries.push({ role: 'error', text: 'That answer was cut short - try asking for less at once.' });
      }

      return;
    }

    this.removeStatus();
    this.entries.push({ role: 'error', text: 'That took too many steps, so it was stopped. Try a narrower question.' });
  }

  //#region Panel status

  private showStatus(calls: any[]): void {
    const labels: { [name: string]: string } = {
      search_bookings: 'Searching bookings',
      list_bookings: 'Reading bookings',
      booking_figures: 'Working out figures',
      get_booking: 'Opening a booking',
      list_packages: 'Reading packages',
      check_availability: 'Checking availability',
      move_booking: 'Preparing the move',
      admission_options: 'Reading admissions',
      change_admissions: 'Preparing the change',
      get_package: 'Reading the package',
      list_time_sets: 'Reading time sets',
      package_building_blocks: 'Reading resources and admissions',
      diagnose_package: 'Checking the package',
      create_package: 'Preparing the package',
      set_start_times: 'Preparing the start times',
      copy_start_times: 'Preparing the start times',
      update_package_settings: 'Preparing the change',
      add_package_to_group: 'Preparing the change',
      create_extra: 'Preparing the extra',
      create_deposit_policy: 'Preparing the deposit policy',
      create_admission: 'Preparing the admission',
      create_warning: 'Preparing the warning',
      create_upgrade: 'Preparing the upgrade',
      create_booking_question: 'Preparing the question',
      create_questionnaire: 'Preparing the questionnaire',
      create_confirmation: 'Preparing the confirmation',
      create_resource: 'Preparing the resource',
      change_package_extras: 'Preparing the change',
      change_package_upgrades: 'Preparing the change',
      change_package_admissions: 'Preparing the change',
      change_time_set_dates: 'Preparing the change',
      find_customer: 'Looking for the customer',
      booking_options: 'Reading the package',
      new_booking_times: 'Checking availability',
      prepare_booking: 'Preparing the booking'
    };

    const text = calls.map(call => labels[call.name] ?? 'Looking things up').filter((label, index, all) => all.indexOf(label) == index).join(', ') + '...';

    this.removeStatus();
    this.entries.push({ role: 'status', text: text });
  }

  private removeStatus(): void {
    this.entries = this.entries.filter(entry => entry.role != 'status');
  }

  private errorText(error: any): string {
    if (error?.status == 0) {
      return 'The assistant relay is not running. Start it from tools/ai-assistant-relay with "npm start".';
    }

    return error?.error?.error ?? error?.message ?? 'Something went wrong.';
  }

  private contextLine(): string {
    const now = new Date();
    const two = (n: number) => ('0' + n).slice(-2);
    const day = now.toLocaleDateString('en-GB', { weekday: 'long' });
    const venue = localStorage.getItem('SmartVenueName') ?? '';
    const currency = localStorage.getItem('Currency') || 'GBP';

    return 'Today is ' + day + ' ' + now.getFullYear() + '-' + two(now.getMonth() + 1) + '-' + two(now.getDate()) +
      (venue != '' ? '. Venue: ' + venue : '') + '. Currency: ' + currency + '.';
  }

  //#endregion

  //#region Tools
  //Names and inputs match the tool list in the relay's server.mjs.

  private async runTool(call: any): Promise<any> {
    try {
      const input = call.input ?? {};
      let result: any;

      switch (call.name) {
        case 'search_bookings': result = await this.searchBookings(input); break;
        case 'list_bookings': result = await this.listBookings(input); break;
        case 'booking_figures': result = await this.bookingFigures(input); break;
        case 'get_booking': result = await this.getBooking(input); break;
        case 'list_packages': result = await this.listPackages(); break;
        case 'check_availability': result = await this.checkAvailability(input); break;
        case 'move_booking': result = await this.proposeMove(input); break;
        case 'admission_options': result = await this.admissionOptions(input); break;
        case 'change_admissions': result = await this.proposeAdmissions(input); break;
        case 'get_package': result = await this.packageAssistant.describePackage(input.package); break;
        case 'list_time_sets': result = await this.packageAssistant.describeTimeSets(); break;
        case 'package_building_blocks': result = await this.packageAssistant.buildingBlocks(); break;
        case 'diagnose_package': result = await this.packageAssistant.diagnose(input); break;
        case 'create_package': result = this.proposePackage(await this.packageAssistant.proposeCreate(input)); break;
        case 'set_start_times': result = this.proposePackage(await this.packageAssistant.proposeStartTimes(input)); break;
        case 'copy_start_times': result = this.proposePackage(await this.packageAssistant.proposeCopyStartTimes(input)); break;
        case 'update_package_settings': result = this.proposePackage(await this.packageAssistant.proposeSettings(input)); break;
        case 'create_extra': result = this.proposePackage(await this.packageAssistant.proposeCreateExtra(input)); break;
        case 'create_deposit_policy': result = this.proposePackage(await this.packageAssistant.proposeCreateDepositPolicy(input)); break;
        case 'create_admission': result = this.proposePackage(await this.packageAssistant.proposeCreateAdmission(input)); break;
        case 'create_warning': result = this.proposePackage(await this.packageAssistant.proposeCreateWarning(input)); break;
        case 'create_upgrade': result = this.proposePackage(await this.packageAssistant.proposeCreateUpgrade(input)); break;
        case 'create_booking_question': result = this.proposePackage(await this.packageAssistant.proposeCreateQuestion(input)); break;
        case 'create_questionnaire': result = this.proposePackage(await this.packageAssistant.proposeCreateQuestionnaire(input)); break;
        case 'create_confirmation': result = this.proposePackage(await this.packageAssistant.proposeCreateConfirmation(input)); break;
        case 'create_resource': result = this.proposePackage(await this.packageAssistant.proposeCreateResource(input)); break;
        case 'change_package_extras': result = this.proposePackage(await this.packageAssistant.proposeExtras(input)); break;
        case 'change_package_upgrades': result = this.proposePackage(await this.packageAssistant.proposeUpgrades(input)); break;
        case 'change_package_admissions': result = this.proposePackage(await this.packageAssistant.proposeAdmissions(input)); break;
        case 'add_package_to_group': result = this.proposePackage(await this.packageAssistant.proposeAddToGroup(input)); break;
        case 'change_time_set_dates': result = this.proposePackage(await this.packageAssistant.proposeTimeSetDates(input)); break;
        case 'find_customer': result = await this.findCustomer(input); break;
        case 'booking_options': result = await this.bookingOptions(input); break;
        case 'new_booking_times': result = await this.newBookingTimes(input); break;
        case 'prepare_booking': result = await this.prepareBooking(input); break;
        default: throw new Error('Unknown tool ' + call.name);
      }

      return { type: 'tool_result', tool_use_id: call.id, content: JSON.stringify(result) };
    }
    catch (error: any) {
      return { type: 'tool_result', tool_use_id: call.id, is_error: true, content: error?.message ?? 'The lookup failed.' };
    }
  }

  private async searchBookings(input: any): Promise<any> {
    const text = (input.text ?? '').toString().trim();

    if (text.length < 2) {
      throw new Error('Search text must be at least 2 characters.');
    }

    const response: any = await this.http.get(bmxurl + 'Booking/Search/' + this.venueId + '/' + encodeURIComponent(text) + '/true').toPromise();
    const rows: any[] = (response?.succeeded ? response.data : null) ?? [];

    return {
      matches: rows.length,
      bookings: rows.slice(0, 30).map(row => this.bookingRow(row)),
      note: rows.length > 30 ? 'Only the first 30 matches are shown - search more specifically.' : undefined
    };
  }

  private async listBookings(input: any): Promise<any> {
    const rows = await this.bookingsInRange(input);

    return {
      count: rows.length,
      bookings: rows.slice(0, 150).map(row => this.bookingRow(row)),
      note: rows.length > 150 ? 'Only the first 150 bookings are listed - use booking_figures for totals, or a shorter range.' : undefined
    };
  }

  private async bookingFigures(input: any): Promise<any> {
    const rows = await this.bookingsInRange(input);
    const round = (value: number) => Math.round(value * 100) / 100;

    const totals = { bookings: 0, guests: 0, sales_value: 0, outstanding: 0, with_allergies: 0, cancelled: 0 };
    const byPackage: { [name: string]: { bookings: number, guests: number, sales_value: number } } = {};
    const byDay: { [day: string]: { bookings: number, guests: number, sales_value: number } } = {};

    rows.forEach(row => {
      if (row.disabled) {
        totals.cancelled++;
        return;
      }

      const guests = Number(row.guests) || 0;
      const value = Number(row.total) || 0;
      const packageName = (row.packageName ?? row.PackageName ?? 'Unknown').toString();
      const day = this.wallClock(row.startTime).substring(0, 10);

      totals.bookings++;
      totals.guests += guests;
      totals.sales_value += value;
      totals.outstanding += Number(row.outstanding) || 0;

      if ((row.allergy ?? '').toString().trim() != '') {
        totals.with_allergies++;
      }

      byPackage[packageName] = byPackage[packageName] ?? { bookings: 0, guests: 0, sales_value: 0 };
      byPackage[packageName].bookings++;
      byPackage[packageName].guests += guests;
      byPackage[packageName].sales_value += value;

      byDay[day] = byDay[day] ?? { bookings: 0, guests: 0, sales_value: 0 };
      byDay[day].bookings++;
      byDay[day].guests += guests;
      byDay[day].sales_value += value;
    });

    totals.sales_value = round(totals.sales_value);
    totals.outstanding = round(totals.outstanding);
    Object.values(byPackage).forEach(item => item.sales_value = round(item.sales_value));
    Object.values(byDay).forEach(item => item.sales_value = round(item.sales_value));

    return {
      from_date: input.from_date,
      to_date: input.to_date,
      date_type: input.date_type ?? 'booking_date',
      cancelled_counted_separately: true,
      totals: totals,
      by_package: byPackage,
      by_day: byDay
    };
  }

  private async getBooking(input: any): Promise<any> {
    const bookingId = await this.resolveBookingId(input.booking);
    const response: any = await this.http.get(bmxurl + 'booking/' + this.venueId + '/' + bookingId).toPromise();

    if (!response?.succeeded || response.data == null) {
      throw new Error('That booking could not be opened.');
    }

    const booking = response.data;
    const customer = booking.customer ?? {};

    return this.compact({
      booking_id: booking.bookingId,
      sales_id: booking.bookingIdHR,
      cancelled: booking.disabled == true,
      part_of_series: (booking.seriesid ?? '') != '' ? true : undefined,
      date_time: this.wallClock(booking.startTime),
      package: booking.PackageName ?? booking.packageName,
      customer: this.compact({ name: customer.fullName ?? booking.name, email: customer.email, mobile: customer.mobile }),
      guests: booking.guests,
      admissions: (booking.resources?.[0]?.admissions ?? [])
        .filter((adm: any) => (Number(adm.noofpeople) || 0) > 0)
        .map((adm: any) => adm.name + ' x ' + adm.noofpeople),
      extras: (booking.extras ?? [])
        .filter((ext: any) => (Number(ext.qty) || 0) > 0)
        .map((ext: any) => ({ name: ext.name, qty: ext.qty, price: ext.price })),
      itinerary: (booking.resources ?? [])
        .filter((res: any) => res.name != null)
        .map((res: any) => (res.name + ' ' + this.wallClock(res.startTime).substring(11)).trim()),
      allergy: booking.allergytext ?? booking.allergy,
      notes: booking.notes,
      question_answers: questionAnswersOn(booking).map(item => ({ question: item.question, answer: item.lines.join('; ') })),
      total: booking.total,
      paid: booking.totalpayments,
      outstanding: booking.outstanding,
      payments: (booking.payments ?? []).map((payment: any) => ({ date: this.wallClock(payment.paymentDate), method: payment.method, amount: payment.amount }))
    });
  }

  private async listPackages(): Promise<any> {
    const response: any = await this.http.get(bmxurl + 'packagelist/' + this.venueId + '/true').toPromise();
    const packages: any[] = (Array.isArray(response) ? response : response?.data) ?? [];

    return packages.map(item => ({ name: item.name, switched_off: item.disabled == true }));
  }

  //#endregion

  //#region Moving a booking
  //Moves are done by the shared MoveBookingService, the same code Edit Booking's Advanced Edit
  //uses. The assistant only ever proposes one; the card's Confirm button makes it.

  private async checkAvailability(input: any): Promise<any> {
    const day = this.parseDay(input.date);

    if (day == null) {
      throw new Error('The date must be YYYY-MM-DD.');
    }

    const loaded = await this.movableBooking(input.booking);
    const slots = await this.moveBookingService.timesOn(this.venueId, loaded, day);

    return {
      sales_id: loaded.booking.bookingIdHR,
      currently: this.wallClock(loaded.booking.startTime),
      date: input.date,
      available_times: slots.map(slot => this.wallClock(slot.time).substring(11))
    };
  }

  //Works out the move and puts it on a card for staff to confirm. Nothing is changed here.
  private async proposeMove(input: any): Promise<any> {
    const day = this.parseDay(input.date);
    const time = (input.time ?? '').toString().trim();

    if (day == null || !/^\d{2}:\d{2}$/.test(time)) {
      throw new Error('The date must be YYYY-MM-DD and the time HH:mm.');
    }

    const loaded = await this.movableBooking(input.booking);
    const booking = loaded.booking;

    if (this.wallClock(booking.startTime) == input.date + ' ' + time) {
      throw new Error('The booking is already at that date and time.');
    }

    const slots = await this.moveBookingService.timesOn(this.venueId, loaded, day);

    if (!slots.some(slot => this.wallClock(slot.time).substring(11) == time)) {
      const free = slots.map(slot => this.wallClock(slot.time).substring(11));
      throw new Error(time + ' is not available on ' + input.date + '. ' + (free.length > 0 ? 'Available times: ' + free.join(', ') + '.' : 'Nothing is available that day.'));
    }

    const customer = booking.customer?.fullName ?? booking.name ?? '';
    const packageName = booking.PackageName ?? booking.packageName ?? '';

    const action: assistantaction = {
      kind: 'move',
      title: 'Move booking',
      icon: 'pi-calendar',
      workingText: 'Checking it is still free and moving...',
      bookingId: booking.bookingId,
      salesId: booking.bookingIdHR,
      summary: 'Move ' + booking.bookingIdHR + (customer != '' ? ' (' + customer + (packageName != '' ? ', ' + packageName : '') + ')' : '') +
        ' from ' + this.showDateTime(this.wallClock(booking.startTime)) + ' to ' + this.showDateTime(input.date + ' ' + time) + '?',
      day: day,
      time: time,
      hasEmail: (booking.customer?.email ?? '').toString().trim() != '',
      status: 'pending'
    };

    this.entries.push({ role: 'action', text: action.summary, action: action });

    return {
      status: 'waiting_for_staff_to_confirm',
      proposal: action.summary,
      instruction: 'Nothing has been changed. A card with Confirm and Cancel is now showing to staff - tell them to press Confirm to make the move. You will be told the outcome with their next message.'
    };
  }

  //The Confirm button.
  async confirmAction(action: assistantaction): Promise<void> {
    if (action.status != 'pending') {
      return;
    }

    action.status = 'working';

    if (action.kind == 'package') {
      await this.confirmPackage(action);
    }
    else if (action.kind == 'booking') {
      await this.confirmBooking(action);
    }
    else if (action.kind == 'admissions') {
      await this.confirmAdmissions(action);
    }
    else {
      await this.confirmMove(action);
    }
  }

  //The booking and the time are checked again first, since either may have changed while the
  //card was waiting.
  private async confirmMove(action: assistantaction): Promise<void> {
    try {
      const loaded = await this.movableBooking(action.bookingId);
      const slots = await this.moveBookingService.timesOn(this.venueId, loaded, action.day);
      const slot = slots.find(item => this.wallClock(item.time).substring(11) == action.time);

      if (slot == null) {
        throw new Error('That time has just been taken, so the booking was not moved.');
      }

      await this.moveBookingService.moveBooking(this.venueId, loaded, slot, action.day);

      //Screens showing either day - the one it left and the one it arrived on - reload.
      this.bookingChanges.announce({
        bookingId: action.bookingId,
        days: [BookingChangesService.dayKeyOfBookingTime(loaded.booking.startTime), BookingChangesService.dayKey(action.day)]
      });

      action.status = 'done';
      action.message = 'Moved.';
      this.notes.push('Staff confirmed the move of ' + action.salesId + ' and it was saved.');
    }
    catch (error: any) {
      action.status = 'failed';
      action.message = error?.message ?? 'The move failed.';
      this.notes.push('Staff tried to confirm the move of ' + action.salesId + ' but it failed: ' + action.message);
    }
  }

  cancelAction(action: assistantaction): void {
    if (action.status != 'pending') {
      return;
    }

    action.status = 'cancelled';
    action.message = 'Cancelled - nothing was changed.';
    this.notes.push(action.kind == 'package'
      ? 'Staff cancelled the proposed change "' + action.title + '": ' + action.summary
      : action.kind == 'booking'
        ? 'Staff cancelled the proposed new booking: ' + action.summary
        : 'Staff cancelled the proposed ' + (action.kind == 'admissions' ? 'admissions change' : 'move') + ' for ' + action.salesId + '.');
  }

  //Package and time set changes are worked out by PackageAssistantService; here they only go on
  //a card, and Confirm runs them.

  private proposePackage(proposal: packageproposal): any {
    const action: assistantaction = {
      kind: 'package',
      title: proposal.title,
      icon: proposal.icon,
      workingText: proposal.workingText,
      bookingId: null,
      salesId: null,
      summary: proposal.summary,
      apply: proposal.apply,
      hasEmail: false,
      status: 'pending'
    };

    this.entries.push({ role: 'action', text: action.summary, action: action });

    return {
      status: 'waiting_for_staff_to_confirm',
      proposal: action.summary,
      instruction: 'Nothing has been changed. A card with Confirm and Cancel is now showing to staff - tell them to check it and press Confirm. You will be told the outcome with their next message.'
    };
  }

  private async confirmPackage(action: assistantaction): Promise<void> {
    try {
      action.message = await action.apply();
      action.status = 'done';
      this.notes.push('Staff confirmed "' + action.title + '" and it was saved: ' + action.message);
    }
    catch (error: any) {
      action.status = 'failed';
      action.message = error?.message ?? 'The change failed.';
      this.notes.push('Staff tried to confirm "' + action.title + '" but it failed: ' + action.message);
    }
  }

  //The booking's admission types: what it has of each now, and what each allows.
  private async admissionOptions(input: any): Promise<any> {
    const loaded = await this.editableBooking(input.booking);

    return {
      sales_id: loaded.booking.bookingIdHR,
      admissions: loaded.admissions.map(adm => this.compact({
        admission_id: adm.admissionid,
        name: adm.name,
        current: Number(adm.noofpeople) || 0,
        min: Number(adm.min) || 0,
        max: adm.max,
        price: adm.price,
        price_is: adm.fixedPrice ? 'per booking' : 'per person',
        set_automatically: adm.fixed == true ? 'yes - worked out from other admissions, cannot be set directly' : undefined
      }))
    };
  }

  //Works out a change of admissions and puts it on a card for staff to confirm. The numbers are
  //checked against each admission's rules and the room checked now, so a change that cannot be
  //made is refused here rather than at Confirm. Nothing is changed.
  private async proposeAdmissions(input: any): Promise<any> {
    const loaded = await this.editableBooking(input.booking);
    const counts = this.countsFrom(loaded, input.changes);
    const before = loaded.admissions.map(adm => ({ admissionid: adm.admissionid, name: adm.name, count: Number(adm.noofpeople) || 0 }));

    this.bookingEditService.setAdmissionCounts(loaded, counts);

    const lines = loaded.admissions
      .map(adm => {
        const was = before.find(item => item.admissionid == adm.admissionid)?.count ?? 0;
        const now = Number(adm.noofpeople) || 0;
        return { name: adm.name, was: was, now: now };
      })
      .filter(line => line.was > 0 || line.now > 0);

    if (!lines.some(line => line.was != line.now)) {
      throw new Error('Those are the numbers the booking already has.');
    }

    //Room is checked now as well as at Confirm, so staff are not offered a change that cannot go
    //through. Done on a separate read so this one stays untouched.
    await this.roomCheck(input.booking, counts);

    const booking = loaded.booking;
    const customer = booking.customer?.fullName ?? '';

    const action: assistantaction = {
      kind: 'admissions',
      title: 'Change admissions',
      icon: 'pi-users',
      workingText: 'Checking there is room and saving...',
      bookingId: booking.bookingId,
      salesId: booking.bookingIdHR,
      summary: 'Change admissions on ' + booking.bookingIdHR + (customer != '' ? ' (' + customer + ')' : '') + ' on ' +
        this.showDateTime(this.wallClock(booking.startTime)) + ':\n' +
        lines.map(line => line.name + ': ' + (line.was == line.now ? line.now + ' (unchanged)' : line.was + ' → ' + line.now)).join('\n'),
      counts: counts,
      hasEmail: (booking.customer?.email ?? '').toString().trim() != '',
      status: 'pending'
    };

    this.entries.push({ role: 'action', text: action.summary, action: action });

    return {
      status: 'waiting_for_staff_to_confirm',
      proposal: action.summary,
      instruction: 'Nothing has been changed. A card with Confirm and Cancel is now showing to staff - tell them to press Confirm to make the change; the price is worked out when it is saved. You will be told the outcome with their next message.'
    };
  }

  //Whether the booking has room for the new numbers at its own time, without saving anything.
  private async roomCheck(wanted: string, counts: { [admissionid: string]: number }): Promise<void> {
    const loaded = await this.editableBooking(wanted);
    this.bookingEditService.setAdmissionCounts(loaded, counts);

    const allowManual = localStorage.getItem('AllowResourcesChangedManually') == 'true';
    const chosen = this.bookingEditService.chosenResources(loaded.selectedPackage.resources, allowManual);
    const req = this.moveBookingService.availabilityRequest(this.venueId, loaded.selectedPackage.packageId, loaded.admissions,
      this.bookingEditService.withUpgradeResources(chosen, loaded.currentUpgrade), loaded.booking.bookingId, new Date(loaded.booking.startTime));

    const response: any = await this.http.post(bmxurl + '/Availability', req).toPromise();

    if (this.bookingEditService.slotAtTime(response?.data ?? [], loaded.booking.startTime) == null) {
      throw new Error('There is not room for that many people at the booking\'s time (' + this.wallClock(loaded.booking.startTime).substring(11) + '). It could be moved to a time with room first.');
    }
  }

  //The Confirm button for admissions: read fresh, set the numbers, and save the way Edit Booking
  //does - which checks for room again at the booking's time.
  private async confirmAdmissions(action: assistantaction): Promise<void> {
    try {
      const loaded = await this.editableBooking(action.bookingId);
      const day = BookingChangesService.dayKeyOfBookingTime(loaded.booking.startTime);

      this.bookingEditService.setAdmissionCounts(loaded, action.counts);

      const allowManual = localStorage.getItem('AllowResourcesChangedManually') == 'true';
      const saved = await this.bookingEditService.saveAdmissions(this.venueId, loaded, allowManual);

      this.bookingChanges.announce({ bookingId: action.bookingId, days: [day] });

      const money = (value: any) => {
        try {
          return new Intl.NumberFormat('en-GB', { style: 'currency', currency: saved?.currency || localStorage.getItem('Currency') || 'GBP' }).format(Number(value) || 0);
        }
        catch {
          return (Number(value) || 0).toFixed(2);
        }
      };

      action.status = 'done';
      action.message = 'Saved.' + (saved?.total != null ? ' New total ' + money(saved.total) + ', outstanding ' + money(saved.outstanding) + '.' : '');
      this.notes.push('Staff confirmed the admissions change for ' + action.salesId + ' and it was saved.' +
        (saved?.total != null ? ' New total ' + money(saved.total) + ', outstanding ' + money(saved.outstanding) + '.' : ''));
    }
    catch (error: any) {
      action.status = 'failed';
      action.message = error?.message ?? 'The change failed.';
      this.notes.push('Staff tried to confirm the admissions change for ' + action.salesId + ' but it failed: ' + action.message);
    }
  }

  //The tool's changes - each an admission (its id or its name) and its new number - as new
  //numbers by admissionid.
  private countsFrom(loaded: bookingforedit, changes: any): { [admissionid: string]: number } {
    if (!Array.isArray(changes) || changes.length == 0) {
      throw new Error('No admissions given to change.');
    }

    const counts: { [admissionid: string]: number } = {};

    changes.forEach((change: any) => {
      const wanted = (change?.admission ?? '').toString().trim().toLowerCase();
      const byId = loaded.admissions.find(adm => (adm.admissionid ?? '').toString().toLowerCase() == wanted);
      const byName = loaded.admissions.filter(adm => (adm.name ?? '').toString().trim().toLowerCase() == wanted);
      const adm = byId ?? (byName.length == 1 ? byName[0] : null);

      if (adm == null) {
        throw new Error('"' + change?.admission + '" is not one of this booking\'s admissions. Use admission_options to see them.');
      }

      counts[adm.admissionid] = Number(change.new_count);
    });

    return counts;
  }

  //A booking read for changing its admissions, refused if it cannot be changed here.
  private async editableBooking(wanted: string): Promise<bookingforedit> {
    const loaded = await this.bookingEditService.loadForEdit(this.venueId, await this.resolveBookingId(wanted));

    if (loaded.booking.disabled == true) {
      throw new Error('That booking is cancelled, so it cannot be changed.');
    }

    if ((loaded.booking.seriesid ?? '') != '') {
      throw new Error('That booking is one date of a repeating package, so its people can only be changed on Edit Series, for every date together.');
    }

    return loaded;
  }

  //Resend Email Confirmation, as on Edit Booking.
  async resendConfirmation(action: assistantaction): Promise<void> {
    if (action.email == 'sending' || action.email == 'sent') {
      return;
    }

    action.email = 'sending';

    try {
      const response: any = await this.http.get(bmxurl + 'confirmationemail/' + this.venueId + '/' + action.bookingId).toPromise();
      action.email = response?.succeeded ? 'sent' : 'failed';
    }
    catch {
      action.email = 'failed';
    }

    if (action.email == 'sent') {
      this.notes.push('Staff resent the confirmation email for ' + action.salesId + '.');
    }
  }

  //A booking read for moving, refused if it cannot be moved here.
  private async movableBooking(wanted: string): Promise<{ booking: any, packageObj: any, admissions: any[] }> {
    const loaded = await this.moveBookingService.loadForMove(this.venueId, await this.resolveBookingId(wanted));

    if (loaded.booking.disabled == true) {
      throw new Error('That booking is cancelled, so it cannot be moved.');
    }

    //The dates of a series are only ever changed together, on Edit Series.
    if ((loaded.booking.seriesid ?? '') != '') {
      throw new Error('That booking is one date of a repeating package, so it can only be changed on Edit Series - not moved on its own.');
    }

    return loaded;
  }

  //#endregion

  //#region Making a booking
  /*
    The assistant prepares a new booking but never makes it. It works out the package, the time
    (checked against real availability, the way Make a Booking asks), the admissions, any extras,
    the customer and notes, and puts them on a card. Confirm opens Make a Booking with each step
    filled in (BookingPrefillService), and staff step through it and take payment there - so the
    price, deposit, booking questions, upgrades and payment are all Make a Booking's own.
  */

  //Customers found by find_customer, by customerid, so prepare_booking can use one by its id.
  private foundCustomers = new Map<string, any>();

  private async findCustomer(input: any): Promise<any> {
    const text = (input.text ?? '').toString().trim();

    if (text.length < 2) {
      throw new Error('Search text must be at least 2 characters.');
    }

    const response: any = await this.http.get(bmxurl + 'customer/search/' + this.venueId + '/' + encodeURIComponent(text)).toPromise();
    const rows: any[] = (Array.isArray(response) ? response : response?.data) ?? [];

    rows.forEach(row => {
      if (row?.customerid != null) {
        this.foundCustomers.set(String(row.customerid), row);
      }
    });

    return {
      matches: rows.length,
      //Everything staff could tell two customers apart by. The id is for prepare_booking only.
      customers: rows.slice(0, 20).map(row => this.compact({
        customer_id: row.customerid,
        name: (row.fullName ?? ((row.firstName ?? '') + ' ' + (row.lastName ?? ''))).toString().trim(),
        email: row.email,
        mobile: row.mobile,
        address: [row.address1, row.address2, row.townCity].map(part => (part ?? '').toString().trim()).filter(part => part != '').join(', '),
        postcode: row.postCode
      })),
      note: rows.length > 20 ? 'Only the first 20 matches are shown - search more specifically.' : undefined
    };
  }

  //The packages that can be booked on a day - what Make a Booking offers for that date.
  private async packagesOn(day: Date): Promise<any[]> {
    const two = (n: number) => ('0' + n).slice(-2);
    const key = two(day.getDate()) + two(day.getMonth() + 1) + day.getFullYear().toString().slice(-2);
    const response: any = await this.http.get(bmxurl + 'PackageGroupList/' + this.venueId + '/false/' + key).toPromise();
    const groups: any[] = response?.data ?? [];
    const packages = new Map<string, any>();

    groups.forEach(group => (group.packages ?? []).forEach((pkg: any) => {
      if (pkg?.packageId != null && !packages.has(String(pkg.packageId))) {
        packages.set(String(pkg.packageId), pkg);
      }
    }));

    return [...packages.values()];
  }

  //One package that can be booked on the day, by its id or its name.
  private async bookablePackage(wanted: any, day: Date, date: string): Promise<any> {
    const packages = await this.packagesOn(day);
    const text = (wanted ?? '').toString().trim().toLowerCase();
    const byId = packages.find(pkg => String(pkg.packageId).toLowerCase() == text);
    const exact = packages.filter(pkg => (pkg.name ?? '').toString().trim().toLowerCase() == text);
    const partial = packages.filter(pkg => (pkg.name ?? '').toString().toLowerCase().includes(text));
    const pkg = byId ?? (exact.length == 1 ? exact[0] : (partial.length == 1 ? partial[0] : null));

    if (pkg == null) {
      const names = packages.map(item => item.name).join(', ');
      throw new Error(partial.length > 1
        ? '"' + wanted + '" matches more than one package on ' + date + ': ' + partial.map(item => item.name).join(', ') + '.'
        : '"' + wanted + '" cannot be booked on ' + date + '. Packages that can: ' + (names != '' ? names : 'none') + '.');
    }

    return pkg;
  }

  //The package's extras offered on the day, as Make a Booking offers them: on for that weekday,
  //not switched off, and on the day for one that is only on a single date. Whether one is variable
  //value comes from the venue's extra list, as Make a Booking reads it.
  private async extrasOn(pkg: any, day: Date): Promise<any[]> {
    const weekday = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][day.getDay()];
    const response: any = await this.http.get(bmxurl + 'ExtraList/' + this.venueId + '/false').toPromise();
    const venueExtras = new Map<string, any>();

    ((response?.data ?? []) as any[]).forEach(ext => venueExtras.set(String(ext.extraId), ext));

    return ((pkg.extras ?? []) as any[])
      .filter(ext => ext[weekday] == true && ext.disabled == false)
      .map(ext => ({ ...ext, singledate: venueExtras.get(String(ext.extraId))?.singledate ?? ext.singledate, variablevalue: (venueExtras.get(String(ext.extraId))?.variablevalue ?? ext.variablevalue) == true }))
      .filter(ext => extraAvailableOnDate({ singledate: ext.singledate }, day));
  }

  private async bookingOptions(input: any): Promise<any> {
    const day = this.parseDay(input.date);

    if (day == null) {
      throw new Error('The date must be YYYY-MM-DD.');
    }

    const pkg = await this.bookablePackage(input.package, day, input.date);
    const extras = await this.extrasOn(pkg, day);

    return this.compact({
      package_id: pkg.packageId,
      package: pkg.name,
      people_min: Number(pkg.min) || undefined,
      people_max: Number(pkg.max) || undefined,
      admissions: ((pkg.admissions ?? []) as any[]).map(adm => this.compact({
        admission_id: adm.admissionid,
        name: adm.name,
        min: Number(adm.min) || 0,
        max: adm.max,
        price: adm.price,
        price_is: adm.fixedPrice ? 'per booking' : 'per person',
        set_automatically: adm.fixed == true ? 'yes - worked out from other admissions, cannot be set directly' : undefined
      })),
      extras: extras.map(ext => this.compact({
        extra_id: ext.extraId,
        name: ext.name,
        price: ext.variablevalue ? 'staff type the value in' : ext.price,
        min: ext.min,
        max: ext.max
      })),
      booking_questions: pkg.questionnaire != null ? 'yes - staff answer them in Make a Booking' : undefined,
      upgrades: (pkg.packageupgrades?.length > 0 || pkg.packageabupgrades?.length > 0) ? 'yes - staff choose them in Make a Booking' : undefined
    });
  }

  //Admission counts from the tool, against the package's own admissions, checked against each
  //admission's limits and the package's limits on people.
  private admissionsFor(pkg: any, wanted: any): { admissionid: string, count: number }[] {
    if (!Array.isArray(wanted) || wanted.length == 0) {
      throw new Error('No admissions given - say how many people on which admissions (booking_options lists them).');
    }

    const admissions: any[] = pkg.admissions ?? [];
    const counts: { admissionid: string, count: number }[] = [];

    wanted.forEach((item: any) => {
      const text = (item?.admission ?? '').toString().trim().toLowerCase();
      const byId = admissions.find(adm => String(adm.admissionid).toLowerCase() == text);
      const byName = admissions.filter(adm => (adm.name ?? '').toString().trim().toLowerCase() == text);
      const adm = byId ?? (byName.length == 1 ? byName[0] : null);
      const count = Number(item?.count);

      if (adm == null) {
        throw new Error('"' + item?.admission + '" is not one of ' + pkg.name + '\'s admissions. Use booking_options to see them.');
      }

      if (adm.fixed == true) {
        throw new Error(adm.name + ' is worked out from the other admissions, so it cannot be set.');
      }

      if (!Number.isInteger(count) || count < 0) {
        throw new Error('The number of people on ' + adm.name + ' must be a whole number.');
      }

      if (count > 0 && Number(adm.min) > 0 && count < Number(adm.min)) {
        throw new Error(adm.name + ' needs at least ' + adm.min + '.');
      }

      if (Number(adm.max) > 0 && count > Number(adm.max)) {
        throw new Error(adm.name + ' allows at most ' + adm.max + '.');
      }

      counts.push({ admissionid: adm.admissionid, count: count });
    });

    //People counted the way Make a Booking counts them against the package's min and max.
    const people = counts
      .filter(item => {
        const adm = admissions.find(a => a.admissionid == item.admissionid);
        return adm?.countstowardsmin || adm?.countstowardsmin == null;
      })
      .reduce((total, item) => total + item.count, 0);

    if (people == 0) {
      throw new Error('The booking needs at least one person.');
    }

    if (Number(pkg.min) > 0 && people < Number(pkg.min)) {
      throw new Error(pkg.name + ' needs at least ' + pkg.min + ' people.');
    }

    if (Number(pkg.max) > 0 && people > Number(pkg.max)) {
      throw new Error(pkg.name + ' takes at most ' + pkg.max + ' people.');
    }

    return counts;
  }

  //The times the package can start on the day for those people, asked for the way Make a Booking
  //asks: the package's resources, the venue's interval setting, and only the times it marks free.
  private async freeTimes(pkg: any, day: Date, counts: { admissionid: string, count: number }[]): Promise<string[]> {
    const req: any = {
      Admissions: ((pkg.admissions ?? []) as any[]).map(adm => ({ ...adm, noofpeople: counts.find(item => item.admissionid == adm.admissionid)?.count ?? 0 })),
      packageId: pkg.packageId,
      venueid: this.venueId,
      StartTime: day.getFullYear() + '-' + (day.getMonth() + 1) + '-' + day.getDate() + 'T00:00:00.000Z',
      DiagnosticMode: true,
      Resources: pkg.resources,
      useintervalavailability: localStorage.getItem('UseIntervalAvailability')
    };

    const response: any = await this.http.post(bmxurl + '/Availability', req).toPromise();

    if (!response?.succeeded) {
      throw new Error(response?.messages?.[0] ?? 'Availability could not be checked.');
    }

    const times = ((response.data ?? []) as any[])
      .filter(slot => slot.timeAvailable)
      .map(slot => (slot.timeString ?? this.wallClock(slot.time).substring(11)).toString());

    return times.filter((time, index) => times.indexOf(time) == index);
  }

  private async newBookingTimes(input: any): Promise<any> {
    const day = this.parseDay(input.date);

    if (day == null) {
      throw new Error('The date must be YYYY-MM-DD.');
    }

    const pkg = await this.bookablePackage(input.package, day, input.date);
    const counts = this.admissionsFor(pkg, input.admissions);

    return { package: pkg.name, date: input.date, available_times: await this.freeTimes(pkg, day, counts) };
  }

  //Works the booking out and puts it on a card. Nothing is booked: Confirm opens Make a Booking
  //filled in.
  private async prepareBooking(input: any): Promise<any> {
    const day = this.parseDay(input.date);
    const time = (input.time ?? '').toString().trim();

    if (day == null || !/^\d{2}:\d{2}$/.test(time)) {
      throw new Error('The date must be YYYY-MM-DD and the time HH:mm.');
    }

    const pkg = await this.bookablePackage(input.package, day, input.date);
    const counts = this.admissionsFor(pkg, input.admissions);
    const free = await this.freeTimes(pkg, day, counts);

    if (!free.includes(time)) {
      throw new Error(time + ' is not available for ' + pkg.name + ' on ' + input.date + '. ' + (free.length > 0 ? 'Available times: ' + free.join(', ') + '.' : 'Nothing is available that day.'));
    }

    //Extras from the package's own list for that day.
    const offered = await this.extrasOn(pkg, day);
    const extras: { extraid: string, qty: number, name: string }[] = [];

    ((input.extras ?? []) as any[]).forEach(item => {
      const text = (item?.extra ?? '').toString().trim().toLowerCase();
      const byId = offered.find(ext => String(ext.extraId).toLowerCase() == text);
      const byName = offered.filter(ext => (ext.name ?? '').toString().trim().toLowerCase() == text);
      const ext = byId ?? (byName.length == 1 ? byName[0] : null);
      const qty = Number(item?.qty);

      if (ext == null) {
        throw new Error('"' + item?.extra + '" is not an extra on ' + pkg.name + ' that day. Use booking_options to see them.');
      }

      if (ext.variablevalue) {
        throw new Error(ext.name + ' has its value typed in by staff, so add it in Make a Booking rather than here.');
      }

      if (!Number.isInteger(qty) || qty < 1) {
        throw new Error('The quantity of ' + ext.name + ' must be a whole number of at least 1.');
      }

      if (Number(ext.max) > 0 && qty > Number(ext.max)) {
        throw new Error(ext.name + ' allows at most ' + ext.max + '.');
      }

      if (Number(ext.min) > 0 && qty < Number(ext.min)) {
        throw new Error(ext.name + ' needs at least ' + ext.min + '.');
      }

      extras.push({ extraid: ext.extraId, qty: qty, name: ext.name });
    });

    const customer = this.customerFor(input.customer);
    const notes = (input.notes ?? '').toString().trim();
    const admissionLines = counts
      .filter(item => item.count > 0)
      .map(item => ((pkg.admissions ?? []) as any[]).find(adm => adm.admissionid == item.admissionid)?.name + ' x ' + item.count);

    const prefill: bookingprefill = {
      packageId: pkg.packageId,
      packageName: pkg.name,
      date: input.date,
      time: time,
      admissions: counts,
      extras: extras.map(ext => ({ extraid: ext.extraid, qty: ext.qty })),
      customer: customer.details,
      notes: notes
    };

    const action: assistantaction = {
      kind: 'booking',
      title: 'New booking',
      icon: 'pi-calendar-plus',
      workingText: 'Opening Make a Booking...',
      bookingId: null,
      salesId: null,
      summary: pkg.name + ' on ' + this.showDateTime(input.date + ' ' + time) + '\n' +
        'People: ' + admissionLines.join(', ') + '\n' +
        (extras.length > 0 ? 'Extras: ' + extras.map(ext => ext.name + ' x ' + ext.qty).join(', ') + '\n' : '') +
        'Customer: ' + customer.text + '\n' +
        (notes != '' ? 'Notes: ' + notes + '\n' : '') +
        'Confirm opens Make a Booking, which fills this in step by step and stops on Notes for you to check it and take payment.',
      prefill: prefill,
      hasEmail: false,
      status: 'pending'
    };

    this.entries.push({ role: 'action', text: action.summary, action: action });

    return {
      status: 'waiting_for_staff_to_confirm',
      proposal: action.summary,
      instruction: 'Nothing has been booked. A card is showing to staff - Confirm opens Make a Booking, which goes through the steps itself and stops on Notes (or wherever staff are needed, such as booking questions) for them to check it and take payment. The price and deposit are worked out there. You will be told what they did with their next message.'
    };
  }

  //The customer for the booking: one found by find_customer, by its id, or a new one's details.
  private customerFor(input: any): { details: bookingprefill['customer'], text: string } {
    if (input == null) {
      throw new Error('No customer given - find one with find_customer, or give a new customer\'s first and last name.');
    }

    const id = (input.customer_id ?? '').toString().trim();

    if (id != '') {
      const found = this.foundCustomers.get(id);

      if (found == null) {
        throw new Error('Customer ' + id + ' was not in the find_customer results - search for them first.');
      }

      return {
        details: {
          customerid: found.customerid, firstName: found.firstName ?? '', lastName: found.lastName ?? '',
          email: found.email, mobile: found.mobile, address1: found.address1, address2: found.address2,
          townCity: found.townCity, postCode: found.postCode
        },
        text: (found.fullName ?? ((found.firstName ?? '') + ' ' + (found.lastName ?? ''))).toString().trim() + ' (existing customer' + (found.email ? ', ' + found.email : '') + ')'
      };
    }

    const firstName = (input.first_name ?? '').toString().trim();
    const lastName = (input.last_name ?? '').toString().trim();

    if (firstName == '' || lastName == '') {
      throw new Error('A new customer needs a first and last name.');
    }

    const details = {
      firstName: firstName, lastName: lastName,
      email: (input.email ?? '').toString().trim(), mobile: (input.mobile ?? '').toString().trim(),
      address1: (input.address1 ?? '').toString().trim(), address2: (input.address2 ?? '').toString().trim(),
      townCity: (input.town ?? '').toString().trim(), postCode: (input.postcode ?? '').toString().trim()
    };

    return {
      details: details,
      text: firstName + ' ' + lastName + ' (new customer' + [details.email, details.mobile].filter(part => part != '').map(part => ', ' + part).join('') + ')'
    };
  }

  //The Confirm button: hands the booking to Make a Booking and opens it there, at the booking's
  //date and time - the time is booked as soon as Make a Booking finds it is still free.
  private async confirmBooking(action: assistantaction): Promise<void> {
    try {
      this.bookingPrefill.set(action.prefill);

      const target = '/booking/0?bookingDate=' + action.prefill.date + 'T' + action.prefill.time + ':00';

      //Already on Make a Booking: it has to be opened afresh to take the booking up.
      if (this.router.url.startsWith('/booking/')) {
        await this.router.navigateByUrl('/home', { skipLocationChange: true });
      }

      await this.router.navigateByUrl(target);

      action.status = 'done';
      action.message = 'Opened in Make a Booking - it stops on Notes for you to check it and take payment.';
      this.notes.push('Staff confirmed the new booking (' + action.prefill.packageName + ' on ' + action.prefill.date + ' ' + action.prefill.time + ') and it was opened in Make a Booking for them to finish and take payment. It is not booked until they do.');
    }
    catch (error: any) {
      this.bookingPrefill.take();
      action.status = 'failed';
      action.message = error?.message ?? 'Make a Booking could not be opened.';
      this.notes.push('Staff tried to confirm the new booking but Make a Booking could not be opened: ' + action.message);
    }
  }

  //#endregion

  //#region Helpers

  //A booking_id as given, or a Sales ID looked up the way the scanner does it.
  private async resolveBookingId(wanted: any): Promise<string> {
    const text = (wanted ?? '').toString().trim().replace('#', '');

    if (text == '') {
      throw new Error('No booking given.');
    }

    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) {
      return text;
    }

    const response: any = await this.http.get(bmxurl + 'Booking/Search/' + this.venueId + '/' + encodeURIComponent(text) + '/true').toPromise();
    const rows: any[] = (response?.succeeded ? response.data : null) ?? [];
    const match = rows.find(row => (row.bookingIdHR ?? '').toLowerCase() == text.toLowerCase()) ?? (rows.length == 1 ? rows[0] : null);

    if (match == null) {
      throw new Error('No booking found for ' + text + '.');
    }

    return match.bookingId;
  }

  //The bookings in a tool's date range, from the same lists the booking list reports use: by the
  //day booked for, or by the day made. Bookings still being made are left out.
  private async bookingsInRange(input: any): Promise<any[]> {
    const from = this.parseDay(input.from_date);
    const to = this.parseDay(input.to_date);

    if (from == null || to == null) {
      throw new Error('Dates must be YYYY-MM-DD.');
    }

    if (to < from) {
      throw new Error('to_date is before from_date.');
    }

    if ((to.getTime() - from.getTime()) / 86400000 > 93) {
      throw new Error('That range is longer than 93 days - split it into shorter ranges.');
    }

    const includeCancelled = input.include_cancelled == true;
    const endpoint = input.date_type == 'created_date' ? '/BookingListCreated' : '/BookingList';

    const response: any = await this.http.post(bmxurl + endpoint, {
      venueid: this.venueId,
      startdate: ConvertDateToDB(from),
      enddate: ConvertDateToDB(to),
      tags: [],
      disabled: includeCancelled,
      created: true
    }).toPromise();

    return ((response?.data ?? []) as any[])
      .filter(row => row.inProgress != true)
      .filter(row => includeCancelled || row.disabled != true);
  }

  //A booking-list row cut down to what is worth reading, with nothing empty sent.
  private bookingRow(row: any): any {
    return this.compact({
      booking_id: row.bookingId,
      sales_id: row.bookingIdHR,
      date_time: this.wallClock(row.startTime),
      made: this.wallClock(row.createddate).substring(0, 10),
      customer: row.name,
      email: row.email,
      phone: row.phone,
      package: row.packageName ?? row.PackageName,
      admissions: row.admissions,
      guests: row.guests,
      total: row.total,
      outstanding: row.outstanding,
      extras: row.extraslist,
      allergy: row.allergy,
      cancelled: row.disabled == true ? true : undefined
    });
  }

  private compact(item: any): any {
    const result: any = {};

    Object.keys(item).forEach(key => {
      const value = item[key];
      const empty = value == null || value === '' || (Array.isArray(value) && value.length == 0) ||
        (typeof value == 'object' && !Array.isArray(value) && Object.keys(value).length == 0);

      if (!empty) {
        result[key] = value;
      }
    });

    return result;
  }

  //Booking times are the venue's wall clock written with a Z on the end, so they are read as
  //written, not moved into the browser's time zone. YYYY-MM-DD HH:mm.
  private wallClock(value: any): string {
    const text = (value ?? '').toString();
    return text.length >= 16 ? text.substring(0, 10) + ' ' + text.substring(11, 16) : text;
  }

  //"2026-10-25 15:00" as staff read it: Sat 25/10/2026 15:00.
  private showDateTime(wallClock: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})$/.exec(wallClock);

    if (match == null) {
      return wallClock;
    }

    const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return day.toLocaleDateString('en-GB', { weekday: 'short' }) + ' ' + match[3] + '/' + match[2] + '/' + match[1] + ' ' + match[4];
  }

  private parseDay(value: any): Date {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').toString().trim());
    return match == null ? null : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  //#endregion
}
